/**
 * Graph Utilities
 *
 * Pure functions for traversing the editor's node/edge graph.
 */

import type { EditorEdge } from './types';

/**
 * Get all descendant node IDs reachable from `rootId` via outgoing edges.
 * Uses BFS traversal following sourceNodeId → targetNodeId direction.
 * The root node itself is NOT included in the result.
 */
export function getDescendants(edges: EditorEdge[], rootId: string): Set<string> {
	// Build adjacency list: sourceNodeId → targetNodeIds
	const children = new Map<string, string[]>();
	for (const edge of edges) {
		let list = children.get(edge.sourceNodeId);
		if (!list) {
			list = [];
			children.set(edge.sourceNodeId, list);
		}
		list.push(edge.targetNodeId);
	}

	const visited = new Set<string>();
	const queue = children.get(rootId);
	if (!queue) return visited;

	// BFS
	const frontier = [...queue];
	for (let i = 0; i < frontier.length; i++) {
		const nodeId = frontier[i];
		if (nodeId === rootId || visited.has(nodeId)) continue;
		visited.add(nodeId);
		const next = children.get(nodeId);
		if (next) {
			for (const child of next) {
				if (!visited.has(child) && child !== rootId) {
					frontier.push(child);
				}
			}
		}
	}

	return visited;
}
